import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  Divider,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InstanceList from './InstanceList';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '../Keyvault/SearchContext';

interface EnvDetails {
  Id: string;
  Tenant_Nm: string;
  Tenant_Desc: string;
  KeyVault_Id: string | null;
}

const TenantDetail: React.FC = () => {
  const { instanceName } = useParams<{ instanceName: string }>();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);

  const location = useLocation();
  const envDetails = location.state?.envDetails;
  const { searchText, setSearchText } = useSearch();

  // Mapping labels so raw keys don't show
  const labelMapping: Record<string, string> = {
    Id: 'ID',
    Tenant_Desc: 'Tenant Description',
    Tenant_Nm: 'Tenant Name',
    KeyVault_Id: 'Key Vault ID',
  };

  return (
    <Box
      sx={{
        bgcolor: '#f5f5f5',
        overflowY: 'auto',
        p: 4,
      }}
    >
      {/* Search Bar */}
      <Box display="flex" justifyContent="center" mb={3}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            style: {
              background: 'white',
              borderRadius: 8,
              minWidth: 300,
              width: '100%',
              maxWidth: 500,
            },
          }}
        />
      </Box>

      {/* Header with back + modules button */}
      <Box display="flex" alignItems="center" mb={3} justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() => navigate('/TenantDetails', { state: { activeTab: 1 } })}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#734bd1', fontWeight: 'bold' }}>
            Tenant Name: {instanceName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: '#734bd1',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#5e3fb0' },
          }}
          onClick={() =>
            navigate(`/TenantDetails/${instanceName}/Modules`, {
              state: {
                envDetails: envDetails,
                tenantId: envDetails?.Id, //  pass tenantId via state
              },
            })
          }
        >
          Enable/Disable Modules
        </Button>
      </Box>

      {/* Collapsible Details Card */}
      <Card sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1}
          sx={{ backgroundColor: '#e0d3ff', cursor: 'pointer' }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <Typography variant="subtitle1">Tenant Details</Typography>
          <IconButton size="small">
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>

        <Collapse in={collapsed} timeout="auto" unmountOnExit>
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              {envDetails &&
                Object.entries(envDetails).map(([key, value]) => {
                  const displayValue = (value ?? '').toString();
                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Box display="flex" gap={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#734bd1', minWidth: 200 }}
                        >
                          {labelMapping[key] || key}
                        </Typography>
                        <Typography sx={{ color: '#734bd1', minWidth: 20 }}>:</Typography>
                        <Tooltip title={displayValue} placement="top-start" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: '#333',
                              maxWidth: 'calc(100% - 160px)',
                            }}
                          >
                            {displayValue}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Instance List Table */}
      {envDetails && <InstanceList tenantId={envDetails.Id} />}
    </Box>
  );
};

export default TenantDetail;
